/**
 * StatusPill -- the one status marker (P124.1 / brand P1.4).
 *
 * RAG status shown as a calm pill on the `rag.*Bg` / `rag.*Line` tokens
 * (never a full card fill). `neutral` is the home for ex-indigo "MVB"
 * labels -- a mono chip, not a coloured interaction tone.
 *
 * Status only -- this is NOT an interaction affordance. Links + buttons
 * use BrassLink / Button.
 */
import type { ComponentPropsWithoutRef, ReactNode } from "react";

export type PillTone = "red" | "amber" | "green" | "neutral";

// P214.3 (Observatory): tones use the new rag.* tokens (auto via P214.1);
// neutral is a mono chip on paper.
const PILL: Record<PillTone, string> = {
  red: "bg-rag-redBg text-rag-red ring-1 ring-rag-redLine",
  amber: "bg-rag-amberBg text-rag-amber ring-1 ring-rag-amberLine",
  green: "bg-rag-greenBg text-rag-green ring-1 ring-rag-greenLine",
  neutral: "bg-paper text-ink-2 ring-1 ring-paper-3 font-mono",
};

interface StatusPillProps extends ComponentPropsWithoutRef<"span"> {
  tone: PillTone;
  /** Render the label in the mono face (used for MVB / code-style chips). */
  mono?: boolean;
  testid?: string;
  children: ReactNode;
}

export function StatusPill({
  tone,
  mono = false,
  testid,
  className = "",
  children,
  ...rest
}: StatusPillProps) {
  return (
    <span
      data-testid={testid}
      data-tone={tone}
      {...rest}
      className={[
        "inline-flex items-center rounded-full px-[10px] py-[3px] text-[10px]",
        "font-semibold uppercase tracking-[.07em]",
        mono ? "font-mono" : "",
        PILL[tone],
        className,
      ]
        .filter(Boolean)
        .join(" ")}
    >
      {children}
    </span>
  );
}
